import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Divider, List, ListItem,
  ListItemIcon, ListItemText, Paper, Stack, Typography
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ReplayIcon from '@mui/icons-material/Replay';
import ChatIcon from '@mui/icons-material/Chat';
import { api } from '../services/api';
import { TutorTopicResourceDTO } from '../services/tutorTopicResourceService';
import { TutorTopicContentDTO } from '../services/tutorTopicContentService';
import { getStudentLifecycle, TopicLifecycleDTO } from '../services/tutorLifecycleService';
import StudentAssessmentSection from '../components/student/StudentAssessmentSection';
import StudentPastPapersSection from '../components/student/StudentPastPapersSection';
import StudentTopicLifecycleBadge from '../components/student/StudentTopicLifecycleBadge';
import StudentTutorQuizSection from '../components/student/StudentTutorQuizSection';
import StudentGnPanel, { StudentGnPanelRef } from '../components/gn/StudentGnPanel';
import InterTopicGnList from '../components/gn/InterTopicGnList';
import ChatPanel from '../components/aicanvas/ChatPanel';

const YOUTUBE_ID_REGEX = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;

function extractYouTubeId(url: string): string | null {
  const match = url.match(YOUTUBE_ID_REGEX);
  return match ? match[1] : null;
}

export default function StudentCourseNodePage() {
  const { courseId, nodeId } = useParams<{ courseId: string; nodeId: string }>();
  const cId = Number(courseId);
  const nId = Number(nodeId);

  const [content, setContent] = useState<TutorTopicContentDTO | null>(null);
  const [resources, setResources] = useState<TutorTopicResourceDTO[]>([]);
  const [loadingContent, setLoadingContent] = useState(true);
  const [loadingResources, setLoadingResources] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [lifecycle, setLifecycle] = useState<TopicLifecycleDTO | null>(null);
  const [revisionFlagged, setRevisionFlagged] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  const personalCanvasRef = useRef<StudentGnPanelRef>(null);
  const [openInterTopicGn, setOpenInterTopicGn] = useState<number | null>(null);

  useEffect(() => {
    api.get<{ data: TutorTopicContentDTO }>(`/api/student/courses/${cId}/nodes/${nId}/content`)
      .then(r => setContent(r.data.data))
      .catch(() => setError('Failed to load content'))
      .finally(() => setLoadingContent(false));

    api.get<{ data: TutorTopicResourceDTO[] }>(`/api/student/courses/${cId}/nodes/${nId}/resources`)
      .then(r => setResources(r.data.data))
      .catch(() => {})
      .finally(() => setLoadingResources(false));

    getStudentLifecycle(cId, nId)
      .then(setLifecycle)
      .catch(() => {});

    api.get<{ data: { flagged: boolean } }>(`/api/student/courses/${cId}/topics/${nId}/revision`)
      .then(r => setRevisionFlagged(r.data.data.flagged))
      .catch(() => {});
  }, [cId, nId]);

  if (loadingContent) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      {(lifecycle || revisionFlagged) && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
          {lifecycle && <StudentTopicLifecycleBadge lifecycle={lifecycle} />}
          {revisionFlagged && (
            <Chip
              icon={<ReplayIcon />}
              label="Revision"
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
        </Stack>
      )}

      {/* Base (non-canvas) topic content. Published tutor canvas renders inside the GN's
          read-only tutor region below (spec 046 FR-035/FR-038). */}
      {content?.format !== 'rich_text' && content?.content ? (
        <Box
          sx={{ mb: 3 }}
          dangerouslySetInnerHTML={{ __html: content.content }}
        />
      ) : null}

      {/* Guruji NoteBook — the student's private notebook with the tutor's published content
          embedded as a read-only region (spec 046 US1). Paste/type/handwrite directly onto
          the notebook; ambient help cues (US2) and answer checking (US4) are built in. */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1, gap: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mr: 'auto' }}>Guruji NoteBook</Typography>
          <Button size="small" startIcon={<ChatIcon />} variant={chatOpen ? 'contained' : 'outlined'} onClick={() => setChatOpen((v) => !v)}>
            Ask AI
          </Button>
        </Box>
        <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 1, height: 520 }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <StudentGnPanel ref={personalCanvasRef} courseId={cId} nodeId={nId} />
          </Box>
          <Collapse in={chatOpen} orientation="horizontal">
            <Paper elevation={0} sx={{ width: 300, height: '100%', borderLeft: '1px solid', borderColor: 'divider' }}>
              <ChatPanel role="student" courseId={cId} nodeId={nId} />
            </Paper>
          </Collapse>
        </Box>
      </Box>

      {/* Student-private notebooks inserted between topics (spec 046 US3, FR-032) */}
      <Box sx={{ mb: 3 }}>
        <InterTopicGnList
          courseId={cId}
          afterNodeId={nId}
          onOpen={(gn) => setOpenInterTopicGn(gn.id)}
        />
        {openInterTopicGn != null && (
          <Box sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, height: 480 }}>
            <StudentGnPanel courseId={cId} nodeId={nId} interTopicGnId={openInterTopicGn} />
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />
      <StudentAssessmentSection courseId={cId} nodeId={nId} />

      <Divider sx={{ my: 3 }} />
      <StudentPastPapersSection courseId={cId} nodeId={nId} />

      <Divider sx={{ my: 3 }} />
      <StudentTutorQuizSection courseId={cId} nodeId={nId} />

      {!loadingResources && resources.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Extra Resources
          </Typography>
          <List dense disablePadding>
            {resources.map(r => {
              if (r.resourceType === 'FILE') {
                return (
                  <ListItem key={r.id} disableGutters>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <InsertDriveFileIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <a
                          href={`/api/resource-files/${r.id}`}
                          download={r.originalFilename ?? undefined}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {r.title || r.originalFilename}
                        </a>
                      }
                    />
                  </ListItem>
                );
              }
              if (r.resourceType === 'YOUTUBE' && r.url) {
                const videoId = extractYouTubeId(r.url);
                return (
                  <ListItem key={r.id} disableGutters sx={{ display: 'block' }}>
                    <Typography variant="body2" fontWeight="medium" mb={0.5}>
                      <VideoLibraryIcon fontSize="small" color="error" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                      {r.title}
                    </Typography>
                    {videoId ? (
                      <Box sx={{ position: 'relative', paddingTop: '56.25%', mb: 1 }}>
                        <iframe
                          title={r.title}
                          src={`https://www.youtube.com/embed/${videoId}`}
                          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </Box>
                    ) : (
                      <a href={r.url} target="_blank" rel="noreferrer">{r.url}</a>
                    )}
                  </ListItem>
                );
              }
              return (
                <ListItem key={r.id} disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <LinkIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <a href={r.url ?? '#'} target="_blank" rel="noreferrer">
                        {r.title || r.url}
                      </a>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </>
      )}
    </Box>
  );
}
